import { createFileRoute } from '@tanstack/react-router'
import { ProductForm } from '@/components/features/products/ProductForm'
import { SessionManager } from '@/lib/auth/session'
import type { ProductWithMedia } from '@pranala/shared'

// API client for fetching product
async function fetchProduct(id: string): Promise<ProductWithMedia | null> {
  try {
    const response = await fetch(`/api/products/${id}`)
    
    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`Failed to fetch product: ${response.status}`)
    }
    
    const result = await response.json()
    return result.product || null
  } catch (error) {
    console.error('Failed to load product for edit:', error instanceof Error ? error.message : error)
    return null
  }
}

export const Route = createFileRoute('/admin/products/$id/edit')({
  beforeLoad: async () => {
    // Check authentication
    const isAuth = await SessionManager.isAuthenticated()
    if (!isAuth) {
      throw new Error('Unauthorized')
    }
  },
  loader: async ({ params }) => {
    const product = await fetchProduct(params.id)
    return { product }
  },
  component: AdminProductEdit,
  errorComponent: ({ error }) => {
    if (error instanceof Error && error.message === 'Unauthorized') {
      window.location.href = '/admin/login'
      return null
    }
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            Error loading product: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        </div>
      </div>
    )
  }
})

function AdminProductEdit() {
  const loaderData = Route.useLoaderData()

  // Provide safe default for hydration
  const product = loaderData?.product

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md">
            Product not found
          </div>
        </div>
      </div>
    )
  }

  return <ProductForm product={product} />
}
